import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  variant?: 'warning' | 'info' | 'danger';
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Conferma',
  cancelText = 'Annulla',
  loading = false,
  variant = 'warning',
}) => {
  if (!isOpen) return null;

  const variantStyles = {
    warning: {
      bg: 'bg-yellow-50',
      icon: 'text-yellow-600',
      button: 'primary' as const,
    },
    info: {
      bg: 'bg-blue-50',
      icon: 'text-blue-600',
      button: 'primary' as const,
    },
    danger: {
      bg: 'bg-red-50',
      icon: 'text-red-600',
      button: 'danger' as const,
    },
  };

  const styles = variantStyles[variant];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative bg-white rounded-xl shadow-2xl max-w-md w-full"
        >
          <div className={`flex items-center justify-between p-6 border-b border-gray-200 ${styles.bg}`}>
            <div className="flex items-center gap-3">
              <AlertCircle size={32} className={styles.icon} />
              <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <X size={24} className="text-gray-700" />
            </button>
          </div>

          <div className="p-6">
            <p className="text-gray-700 mb-6">{message}</p>

            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={onClose}
                disabled={loading}
              >
                {cancelText}
              </Button>
              <Button
                variant={styles.button}
                onClick={onConfirm}
                loading={loading}
              >
                {confirmText}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
